/*
  barna flow — bundle progress bar for the cart drawer and cart page (snippets/s2-cart-progress.liquid).

  The tiers are not stored in the theme. For each product in the cart this reads the Kaching deal from
  /products/<handle>?view=kaching-deal (templates/product.kaching-deal.json: Kaching's own app block on a bare layout, which lets Kaching's app embed print the deal, and
  nothing else) and uses exactly what Kaching prints there: quantity per tier, discount per item, free
  shipping and gifts. Change the deal in Kaching and the bar follows; nothing to edit here.

  Counting follows Kaching: only products that carry the deal count, and free gift lines (final price 0)
  never do. With socks and scrunchies in one cart (two deals), the bar follows the deal with more items.
  The euro total already saved comes from the cart itself: Shopify's discount allocations plus any sale
  price (compare-at above price). Each tier's saving adds that sale gap per item to Kaching's discount, so
  a 2-pair tier of €3 off each on socks at €17.95 (compare-at €19.95) reads €10, as Kaching's own block
  on the product page shows it. A free gift Kaching counts in the saving ("Include in compare-at") adds
  its price too, so 4 pairs with the silk scrunchie reads €36.95, Kaching's figure. With different sale gaps in one deal, the smallest one counts, so the bar
  never promises more than the cart will show.
  If no deal can be read, the bar stays hidden rather than promise something Kaching won't apply.

  <s2-deal-progress> runs on every insert, so it redraws itself whenever cart.js re-renders the drawer.
*/

(() => {
  if (customElements.get('s2-deal-progress')) return;

  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const TTL = 10 * 60 * 1000; // a Kaching change reaches a shopper already browsing within 10 minutes
  const inflight = new Map();

  const readCache = (handle) => {
    if (window.Shopify && window.Shopify.designMode) return undefined;
    try {
      const hit = JSON.parse(sessionStorage.getItem(`s2deal2:${handle}`) || 'null');
      if (hit && Date.now() - hit.t < TTL) return hit.d;
    } catch (e) { /* storage blocked: fetch instead */ }
    return undefined;
  };
  const writeCache = (handle, deal) => {
    try { sessionStorage.setItem(`s2deal2:${handle}`, JSON.stringify({ t: Date.now(), d: deal })); } catch (e) { /* ignore */ }
  };

  // "2 pairs" -> "pairs": the unit word the product page already showed the shopper.
  const unitFrom = (title) => {
    const m = typeof title === 'string' && title.match(/^\s*\d+\s+([^<>{}]+?)\s*$/);
    return m ? m[1] : null;
  };

  // The free gifts Kaching counts in a tier's saving ("Include in compare-at"), as [product GIDs,
  // quantity] pairs: a gift picker offers several products, one of which the shopper gets.
  const countedGifts = (b) => [
    ...(b.multipleGiftsSelectors || [])
      .filter((s) => s.includeInCompareAt)
      .map((s) => [(s.products || []).map((p) => p.id), Number(s.maxQuantity) || 1]),
    ...(b.freeGifts || [])
      .filter((g) => g.giftType !== 'shipping' && g.includeInCompareAt && g.productGID)
      .map((g) => [[g.productGID], Number(g.quantity) || 1]),
  ];

  const normalise = (raw) => {
    const bars = (raw.dealBars || []).filter((b) => b.dealBarType === 'quantity-break' || !b.dealBarType);
    const tiers = bars
      .map((b) => ({
        qty: Number(b.quantity) || 0,
        type: b.discountType,
        value: Number(b.discountValue) || 0,
        ship: (b.freeGifts || []).some((g) => g.giftType === 'shipping'),
        gift:
          (b.freeGifts || []).some((g) => g.giftType && g.giftType !== 'shipping') ||
          (b.multipleGiftsSelectors || []).length > 0,
        gifts: countedGifts(b),
      }))
      .filter((t) => t.qty > 0)
      .sort((a, b) => a.qty - b.qty);
    const one = bars.find((b) => Number(b.quantity) === 1);
    const many = bars.find((b) => Number(b.quantity) > 1);
    return {
      id: raw.id,
      unitOne: one ? unitFrom(one.title) : null,
      unitMany: many ? unitFrom(many.title) : null,
      tiers,
    };
  };

  // Prices of the counted gifts, in shop-currency cents, from Shopify's public Storefront API (no
  // token needed for products). Kaching names gifts only by product, so this is where their price
  // comes from. A gift picker counts its cheapest product, so the bar never promises more than the
  // cart will show; if the prices can't be read, gifts count as 0 and the bar shows the discount alone.
  const STOREFRONT_API = '/api/2026-07/graphql.json';
  const priceGifts = async (deal) => {
    const ids = [...new Set(deal.tiers.flatMap((tier) => tier.gifts.flatMap(([products]) => products)))];
    const prices = new Map();
    if (ids.length) {
      try {
        const response = await fetch(STOREFRONT_API, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            query: 'query($ids:[ID!]!){nodes(ids:$ids){... on Product{id priceRange{minVariantPrice{amount}}}}}',
            variables: { ids },
          }),
        });
        const { data } = await response.json();
        (data?.nodes || []).forEach((node) => {
          const amount = Number(node?.priceRange?.minVariantPrice?.amount);
          if (node?.id && amount > 0) prices.set(node.id, Math.round(amount * 100));
        });
      } catch (e) { /* no prices: gifts count as 0 */ }
    }
    deal.tiers.forEach((tier) => {
      tier.giftValue = tier.gifts.reduce((sum, [products, quantity]) => {
        const known = products.map((id) => prices.get(id)).filter(Boolean);
        return sum + (known.length ? Math.min(...known) * quantity : 0);
      }, 0);
    });
    return deal;
  };

  const getDeal = (handle) => {
    const cached = readCache(handle);
    if (cached !== undefined) return Promise.resolve(cached);
    if (inflight.has(handle)) return inflight.get(handle);
    const request = fetch(`${root}products/${encodeURIComponent(handle)}?view=kaching-deal`, { credentials: 'same-origin' })
      .then((r) => (r.ok ? r.text() : ''))
      .then(async (html) => {
        const m = html.match(/<script class="kaching-bundles-deal-block-settings"[^>]*>([\s\S]*?)<\/script>/);
        const deal = m ? await priceGifts(normalise(JSON.parse(m[1]))) : null;
        writeCache(handle, deal);
        return deal;
      })
      .catch(() => null)
      .finally(() => inflight.delete(handle));
    inflight.set(handle, request);
    return request;
  };

  const formatMoney = (cents, format) => {
    const whole = Math.round(cents) % 100 === 0;
    const fixed = (n, sep, dec) => {
      const [i, d] = (n / 100).toFixed(whole ? 0 : 2).split('.');
      return i.replace(/\B(?=(\d{3})+(?!\d))/g, sep) + (d ? dec + d : '');
    };
    return (format || '€{{amount_with_comma_separator}}').replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      if (key.includes('comma_separator')) return fixed(cents, '.', ',');
      if (key.includes('apostrophe')) return fixed(cents, "'", '.');
      if (key.includes('space_separator')) return fixed(cents, ' ', ',');
      return fixed(cents, ',', '.');
    });
  };

  const escapeHtml = (s) => String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  const fill = (template, values) =>
    template.replace(/\[(\w+)\]/g, (whole, key) => (key in values ? values[key] : whole)).replace(/\s*(\+|·)\s*$/, '').trim();

  const check = '<svg viewBox="0 0 16 16" width="12" height="12" focusable="false"><path d="M3.5 8.5l3 3 6-7" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>';

  class S2DealProgress extends HTMLElement {
    connectedCallback() {
      this.render().catch((error) => {
        console.error('[s2-deal-progress]', error);
        this.hide();
      });
    }

    hide() {
      this.hidden = true;
      this.setShipping(false);
    }

    setShipping(free) {
      const scope = this.closest('.shopify-section') || document;
      scope.querySelectorAll('[data-s2-deal-shipping]').forEach((el) => {
        el.textContent = free ? el.dataset.free : el.dataset.pending;
        el.classList.toggle('s2cd-summary__free', free);
        el.classList.toggle('s2cd-summary__muted', !free);
      });
    }

    async render() {
      const data = JSON.parse(this.querySelector('script[type="application/json"]').textContent);
      const t = data.text;
      // Kaching's tier titles name the unit in English ("2 pairs"). The block's "Unit words" setting maps
      // those words to stored, translatable ones ("pairs = pares"); a word not listed shows as Kaching wrote it.
      const unitWords = new Map(
        String(t.unit_words || '')
          .split(/\r?\n/)
          .map((line) => line.split('='))
          .filter((pair) => pair.length === 2 && pair[0].trim() && pair[1].trim())
          .map(([from, to]) => [from.trim().toLowerCase(), to.trim()])
      );
      const unitWord = (word) => unitWords.get(String(word).toLowerCase()) || word;
      const rate = Number(window.Shopify && window.Shopify.currency && window.Shopify.currency.rate) || 1;
      const money = (eurCents) => formatMoney(eurCents * rate, data.money_format);

      // Gift lines are free by definition; they never count toward a tier.
      const lines = data.lines.filter((l) => !(l.final === 0 && l.original > 0));
      const handles = [...new Set(lines.map((l) => l.handle))];
      const deals = await Promise.all(handles.map(getDeal));

      const groups = new Map();
      handles.forEach((handle, i) => {
        const deal = deals[i];
        if (!deal || !deal.tiers.length) return;
        const group = groups.get(deal.id) || { deal, qty: 0, sale: Infinity };
        lines
          .filter((l) => l.handle === handle)
          .forEach((l) => {
            group.qty += l.qty;
            // Sale gap per item in shop-currency cents, like Kaching's discount values.
            group.sale = Math.min(group.sale, Math.max(0, ((l.list || l.original) - l.original) / l.qty / rate));
          });
        groups.set(deal.id, group);
      });
      const best = [...groups.values()].sort((a, b) => b.qty - a.qty)[0];
      if (!best) return this.hide();

      const { deal, qty, sale } = best;
      const steps = deal.tiers.filter((tier) => tier.value > 0 || tier.ship || tier.gift);
      if (!steps.length) return this.hide();
      const max = steps[steps.length - 1].qty;
      const current = [...deal.tiers].reverse().find((tier) => tier.qty <= qty) || null;
      const next = steps.find((tier) => tier.qty > qty) || null;

      // Like Kaching's "You save": the discount, the sale gap, and any gift it counts at its price.
      const amountOf = (tier) => {
        if (tier.type === 'amount') return money(tier.qty * (tier.value * 100 + sale) + (tier.giftValue || 0));
        if (tier.type === 'percentage') return `${tier.value}%`;
        return null;
      };
      const perksOf = (tier, since) =>
        [tier.ship && !(since && since.ship) ? t.perk_shipping : null, tier.gift && !(since && since.gift) ? t.perk_gift : null].filter(Boolean);
      const rewardOf = (tier, since) =>
        [amountOf(tier) ? fill(t.amount_off, { amount: amountOf(tier) }) : null, ...perksOf(tier, since)].join(' + ');

      const count = next ? next.qty - qty : 0;
      const unit = unitWord(count === 1 ? deal.unitOne || t.item_singular : deal.unitMany || t.item_plural);
      const saved = `<strong>${escapeHtml(money(data.saved / rate))}</strong>`;
      let message;
      // The next reward leads, in a goal box: "Add 1 more pair to unlock" over chips for what that
      // tier adds (its saving, then free shipping or the free gift if it brings them), with what's
      // saved so far underneath. Once the top tier is reached it says so, with everything earned.
      // Without the s2_cart.progress_* wording (an older locale) the one-line message stays.
      const icons = {
        gift: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><rect x="3" y="8" width="18" height="4" rx="1"/><path d="M12 8v13M19 12v7a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2v-7"/><path d="M7.5 8a2.5 2.5 0 0 1 0-5C11 3 12 8 12 8s1-5 4.5-5a2.5 2.5 0 0 1 0 5"/></svg>',
        ship: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M3 6h11v10H3zM14 9h4l3 3v4h-7"/><circle cx="7" cy="17" r="2"/><circle cx="17" cy="17" r="2"/></svg>',
        save: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M20.6 13.4l-7.2 7.2a2 2 0 0 1-2.8 0L3 13V3h10l7.6 7.6a2 2 0 0 1 0 2.8z"/><circle cx="7.5" cy="7.5" r="1.5"/></svg>',
      };
      const chip = (text, kind) =>
        `<span class="s2cd-progress__chip s2cd-progress__chip--${kind}">${kind === 'save' ? '' : icons[kind]}<span>${escapeHtml(text)}</span></span>`;
      const chipsFor = (tier, since) =>
        [
          amountOf(tier) ? chip(fill(t.chip_save || t.label_amount, { amount: amountOf(tier) }), 'save') : '',
          tier.ship && !(since && since.ship) ? chip(t.chip_shipping || t.perk_shipping, 'ship') : '',
          tier.gift && !(since && since.gift) ? chip(t.chip_gift || t.perk_gift, 'gift') : '',
        ].join('');
      if (t.unlock && (next || current)) {
        const goal = next || current;
        const lead = next
          ? fill(escapeHtml(t.unlock), { count: `<strong>${count}</strong>`, items: escapeHtml(unit) })
          : escapeHtml(t.unlocked);
        const so_far = next && data.saved > 0 ? `<p class="s2cd-progress__sub">${fill(escapeHtml(t.so_far), { saved })}</p>` : '';
        message = `<div class="s2cd-progress__goal${next ? '' : ' is-done'}">
          <span class="s2cd-progress__goal-icon">${goal.gift ? icons.gift : goal.ship ? icons.ship : icons.save}</span>
          <div class="s2cd-progress__goal-text">
            <p class="s2cd-progress__lead">${lead}</p>
            <p class="s2cd-progress__chips">${next ? chipsFor(next, current) : chipsFor(current, null)}</p>
          </div>
        </div>${so_far}`;
      } else if (next && data.saved > 0) {
        message = fill(escapeHtml(t.message_next), { saved, count, items: escapeHtml(unit), reward: `<strong>${escapeHtml(rewardOf(next, current))}</strong>` });
      } else if (next) {
        message = fill(escapeHtml(t.message_start), { count, items: escapeHtml(unit), reward: `<strong>${escapeHtml(rewardOf(next, current))}</strong>` });
      } else {
        const perks = perksOf(current).join(' + ');
        message = fill(escapeHtml(t.message_done), { saved, perks: escapeHtml(perks) });
      }

      const pct = (n) => Math.min(100, (n / max) * 100);
      const markers = steps
        .map((tier, i) => {
          const reached = qty >= tier.qty;
          return `<span class="s2cd-progress__marker${reached ? ' is-reached' : ''}${tier === next ? ' is-next' : ''}${i === steps.length - 1 ? ' is-last' : ''}" style="left:${pct(tier.qty)}%" aria-hidden="true">${reached ? check : tier.qty}</span>`;
        })
        .join('');
      const labels = t.show_labels
        ? `<div class="s2cd-progress__labels" aria-hidden="true">${steps
            .map((tier, i) => {
              const prev = i ? steps[i - 1] : null;
              const gap = Math.min(tier.qty - (prev ? prev.qty : 0), steps[i + 1] ? steps[i + 1].qty - tier.qty : Infinity);
              const amount = amountOf(tier);
              const perks = perksOf(tier, prev).map((p) => `+ ${escapeHtml(p)}`).join('<br>');
              return `<span class="s2cd-progress__label${qty >= tier.qty ? ' is-reached' : ''}" style="left:${pct(tier.qty)}%;max-width:calc(${(gap / max) * 100}% - 6px)">${
                amount ? escapeHtml(fill(t.label_amount, { amount })) : ''
              }${perks ? `<small>${perks}</small>` : ''}</span>`;
            })
            .join('')}</div>`
        : '';

      this.querySelector('[data-s2-deal-body]').innerHTML = `
        ${t.unlock ? message : `<p class="s2cd-progress__message">${message}</p>`}
        <div class="s2cd-progress__track" role="progressbar" aria-valuemin="0" aria-valuemax="${max}" aria-valuenow="${Math.min(qty, max)}" aria-label="${escapeHtml(unitWord(deal.unitMany || t.item_plural))}">
          <span class="s2cd-progress__fill" style="width:${pct(qty)}%"></span>${markers}
        </div>${labels}`;
      this.classList.toggle('s2cd-progress--done', !next);
      this.hidden = false;
      this.setShipping(Boolean(current && current.ship));
      this.fitLabels();
    }

    // Labels are absolutely placed under their markers, so their row can't size itself. Fit it to the
    // tallest label whenever a label's size changes: when the closed drawer first opens (nothing has a
    // size before that) and when the language changes the label text (Spanish and German run longer). While nothing has a size
    // yet, the stylesheet's height stands.
    fitLabels() {
      const row = this.querySelector('.s2cd-progress__labels');
      if (!row) return;
      const fit = () => {
        const tallest = Math.max(0, ...[...row.children].map((label) => label.offsetHeight));
        row.style.height = tallest > 0 ? `${tallest}px` : '';
      };
      this.labelObserver?.disconnect();
      this.labelObserver = new ResizeObserver(fit);
      [...row.children].forEach((label) => this.labelObserver.observe(label));
    }

    disconnectedCallback() {
      this.labelObserver?.disconnect();
    }
  }

  customElements.define('s2-deal-progress', S2DealProgress);
})();
